import React, { useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Outlet, useNavigate } from "react-router-dom";
import { PlusOutlined, ArrowLeftOutlined, DownOutlined } from "@ant-design/icons";
import { Row, Col, Modal, Space, Button, Dropdown, Typography } from "antd";

import type { MenuProps } from "antd";
import { openNotificationWithIcon } from "utils";
import { useAppSelector, useAppDispatch } from "app/hooks";

import ErrorFallback from "components/ErrorFallback";
import RequeryMultipleBatchDrawer from "pages/Generation/components/RequeryMultipleBatchDrawer";
import InvalidateMultipleBatchDrawer from "pages/Generation/components/InvalidateMultipleBatchDrawer";

import {
  setIsRequeryMultipleBatchDrawerOpen,
  setIsInvalidateMultipleBatchDrawerOpen,
} from "./generationSlice";
import {
  useGetServersQuery,
  useGetNotificationQuery,
  useInitiateExtractionMutation,
} from "./generationApiSlice";
import {
  useGetNIBSSStatusEnumQuery,
  useGetSortCodeTypeEnumQuery,
  useGetTransactionTypeEnumQuery,
} from "app/configApiSlice";

import { TableWrapper, DataGenerationContainer } from "./styled";

const { Title } = Typography;

const DataGeneration: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const servers = useGetServersQuery();
  const sortCodeEnum = useGetSortCodeTypeEnumQuery();
  const NIBSSStatusEnum = useGetNIBSSStatusEnumQuery();
  const websocketNotification = useGetNotificationQuery();
  const transactionTypeEnum = useGetTransactionTypeEnumQuery();
  const { layout } = useAppSelector((state) => state.generation);

  const [modalVisible, setModalVisible] = useState(false);
  const [server, setServer] = useState({ id: "", serverLocation: "" });

  const [initiateExtraction, initiateExtractionResult] = useInitiateExtractionMutation();

  const handleAdminMenuClick: MenuProps["onClick"] = (e) => {
    if (e.key === "refresh-multiple-batches") {
      dispatch(setIsRequeryMultipleBatchDrawerOpen(true));
    }

    if (e.key === "invalidate-multiple-batches") {
      dispatch(setIsInvalidateMultipleBatchDrawerOpen(true));
    }
  };

  const handleInitiateExtraction = async (serverId: string) => {
    initiateExtraction({ serverId })
      .unwrap()
      .then((payload) => {
        openNotificationWithIcon("Successful", payload.successMessage, "success");
        setModalVisible(false);
      })
      .catch((error) => {
        setModalVisible(false);
        if (error.status >= 500 && error.status < 600) {
          openNotificationWithIcon(error.data.error, error.data.message, "error");
        } else if (error.status === 400) {
          error.data?.errorMessageList?.length
            ? error.data?.errorMessageList?.forEach((errorMessage: string) =>
                openNotificationWithIcon(error.data.errorMessage, errorMessage, "warning"),
              )
            : openNotificationWithIcon(error.data.errorMessage, "", "warning");
        }
      });
  };

  useEffect(() => {
    if (servers.isError) {
      // Handle server error if needed
    }
  }, [servers.isFetching, servers.isError, websocketNotification.isError]);

  const items: MenuProps["items"] = [
    {
      label: "Invalidate Multiple Batches",
      key: "invalidate-multiple-batches",
    },
    {
      label: "Requery Multiple Batches",
      key: "refresh-multiple-batches",
    },
  ];

  const menuProps = {
    items,
    onClick: handleAdminMenuClick,
  };

  const serverItems: MenuProps["items"] = servers.data?.response
    ? servers.data?.response?.map(({ id, serverLocation }) => ({
        key: id,
        label: serverLocation,
        disabled: initiateExtractionResult.isLoading,
        onClick: () => setServer({ id, serverLocation }),
      }))
    : [];

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <DataGenerationContainer>
        <Modal
          okText="Yes"
          open={modalVisible}
          maskClosable={false}
          title="Select Server"
          onCancel={() => setModalVisible(false)}
          closable={!initiateExtractionResult.isLoading}
          onOk={() => handleInitiateExtraction(server.id)}
          confirmLoading={initiateExtractionResult.isLoading}
          cancelButtonProps={{
            disabled: initiateExtractionResult.isLoading,
          }}
        >
          <Typography.Text style={{ fontSize: "14px" }}>
            Are you sure you want to select {server.serverLocation}
          </Typography.Text>
        </Modal>
        <Row>
          <Col className="heading" span={24}>
            <Title level={4}>{layout.title}</Title>
          </Col>
        </Row>

        <Row justify="space-between">
          <Col>
            <Button
              size="large"
              shape="circle"
              style={{ marginTop: 15 }}
              onClick={() => navigate(-1)}
              icon={<ArrowLeftOutlined />}
            ></Button>
          </Col>
          <Col>
            <Row align="middle" gutter={15}>
              <Col style={{ marginTop: 15 }}>
                {layout.function === "HOME" && (
                  <Dropdown menu={menuProps}>
                    <Button size="large">
                      <Space>
                        Admin Actions
                        <DownOutlined />
                      </Space>
                    </Button>
                  </Dropdown>
                )}
              </Col>

              <Col style={{ marginTop: 15 }}>
                {layout.function === "HOME" && (
                  <Dropdown
                    trigger={["click"]}
                    placement="bottomRight"
                    menu={{
                      items: serverItems,
                      disabled: servers.isLoading,
                      expandIcon: <DownOutlined />,
                      onClick: () => setModalVisible(true),
                    }}
                  >
                    <Button
                      size="large"
                      shape="circle"
                      icon={<PlusOutlined />}
                      loading={servers.isLoading}
                      disabled={servers.isLoading}
                    ></Button>
                  </Dropdown>
                )}
              </Col>
            </Row>
          </Col>
        </Row>

        <TableWrapper>
          <Outlet
            context={{
              sortCode: sortCodeEnum.data,
              NIBSSStatus: NIBSSStatusEnum.data,
              transactionType: transactionTypeEnum.data,
            }}
          />
        </TableWrapper>

        <RequeryMultipleBatchDrawer />
        <InvalidateMultipleBatchDrawer />
      </DataGenerationContainer>
    </ErrorBoundary>
  );
};

export default DataGeneration;
